// The pre-auth surface: paths that must render WITHOUT a Keycloak session.
//
// Everything else in this SPA is `onLoad: 'login-required'` — a hard bounce to
// the realm's login page before the app mounts. The identity funnel cannot
// live under that rule: its entire purpose is serving people who have no
// account yet (sign-up), no account in THIS estate yet (claim), or no way back
// into the one they have (recovery). Core's claim surface is a pre-auth REST
// API for exactly the same reason (SPEC.md U5).
//
// On these paths src/boot/keycloak.ts skips Keycloak initialisation entirely
// rather than attempting a silent `check-sso`: the funnel needs no token, and a
// prompt=none round trip to an unreachable or third-party-cookie-blocked
// Keycloak would turn "sign up" into a blank page. This applies at PAGE LOAD
// only — an already-authenticated user who routes to /account-info client-side
// keeps their session, because no boot runs.
//
// Kept in `src/core/` (not the identity module) because the auth boot consumes
// it, and the boot must not depend on a lazily-loaded module.
import { isCmsCandidatePath } from './reservedPaths'

/** Route paths served without authentication. Order is irrelevant; matching is
 *  segment-exact-or-prefix (see isPublicPath). */
export const PUBLIC_PATHS = [
  '/signin',
  '/claim',
  '/signup',
  '/recovery',
  '/setup',
  '/account-info',
  // Document-verification QR (fsgadmin/fsgnext#33, register D): reached by a
  // printed QR code or a shared link, never by nav — same "no session yet"
  // shape as the identity funnel above, but not an identity page itself.
  // '/verify' is TYPO3's own prefix, kept verbatim (D12).
  '/verify',
  // The portal's own /documents deep link (fsgadmin/fsgnext#69 §E4) — same
  // "no session yet" shape as /verify. Distinct from the printed website URL
  // of the same shape (§E3); see router/routes.ts's docs comment.
  '/documents',
  '/rules/faq',
  // The public website (redesign batch 5). '/' matches only itself — the
  // segment-boundary test below never treats it as a prefix.
  '/',
  '/event',
  '/teams',
  '/results',
  // The News module's public pages (#162 AH6/AH10): the list, the archive,
  // the categories and every article, at the TYPO3 addresses.
  '/pr/news',
  // The partners app page (#168 AP6); the partner pages below it are CMS
  // candidates, public anyway.
  '/partners',
  // World › Competitions (#170); the other `/world/…` pages are CMS
  // candidates, public anyway.
  '/world/competitions',
  // The announcements board (#164): anonymous, dark until cutover.
  '/today',
] as const

/** Public paths that still pick up an existing session on a direct page load:
 *  a silent `check-sso` against the stored realm, falling back to pre-auth
 *  (#123 S2(d), src/boot/keycloak.ts). The dual-audience pages (#123 S4: the
 *  FAQ, /verify, the /documents deep link) render in the main layout for both
 *  audiences, so a signed-in visitor must get the signed-in page there; the
 *  FAQ needs it all the more because rules serves it to signed-in readers
 *  only while the anonymous tier is switched off (#111). The funnel pages are
 *  not here: they serve people without a session. A visitor who never chose a
 *  realm is not redirected. */
const SESSION_AWARE_PUBLIC_PATHS = [
  '/rules/faq',
  '/verify',
  '/',
  '/event',
  '/teams',
  '/results',
  '/documents',
  '/pr/news',
  '/partners',
  '/world/competitions',
  '/today',
] as const

/** One-shot: the session-aware public path the visitor asked to sign in on.
 *  Its next direct load runs `login-required` instead of `check-sso`, because a
 *  silent check never shows a login and would send them straight back to the
 *  anonymous page. */
export const LOGIN_INTENT_KEY = 'portal.loginIntent'

/** Where an interrupted navigation is remembered while the visitor picks a
 *  realm, so the chooser can put them back where they were aiming. */
export const RETURN_TO_KEY = 'portal.returnTo'

/** Remember where to come back to and that the next load of `path` must log
 *  in, not only check silently (see LOGIN_INTENT_KEY). Call before routing a
 *  visitor on a session-aware public page to /signin. */
export function rememberSignIn(path: string, fullPath: string): void {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, fullPath)
    sessionStorage.setItem(LOGIN_INTENT_KEY, path)
  } catch {
    // No storage: /signin still works, it just lands on the dashboard.
  }
}

/**
 * Whether `path` is served pre-auth.
 *
 * Prefix matching is on SEGMENT boundaries: `/claim/email` is public because
 * it is below `/claim`, but a hypothetical `/claims` is not — a substring test
 * would have quietly opened it.
 */
export function isPublicPath(path: string): boolean {
  return matchesAny(path, PUBLIC_PATHS) || isCmsCandidatePath(path)
}

/** Whether a direct load of `path` checks for an existing session first (see
 *  SESSION_AWARE_PUBLIC_PATHS). Same segment-boundary matching.
 *
 *  Every path outside the app's own URL space is session-aware too: it is a
 *  candidate for a CMS page (#160 AF10, src/core/reservedPaths.ts), which an
 *  anonymous visitor reads on the public token and a signed-in one on their
 *  session (AF14) — a gated page opens for a member without a second sign-in.
 *  With the `cms` feature off such a path answers the 404 page either way. */
export function isSessionAwarePublicPath(path: string): boolean {
  return matchesAny(path, SESSION_AWARE_PUBLIC_PATHS) || isCmsCandidatePath(path)
}

function matchesAny(path: string, prefixes: readonly string[]): boolean {
  const normalized = path.replace(/\/+$/, '') || '/'
  return prefixes.some((p) => normalized === p || normalized.startsWith(`${p}/`))
}
